import { useState, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FaPlus, FaTrash, FaVolumeUp, FaSpinner, FaEye, FaLanguage } from 'react-icons/fa';
import { db } from '../../../firebase';
import { translateBatchToText } from '../../../utilities/extensions/translateToText';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import Constants from '../../../utilities/constants/Constants';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const LongQuestionAnswersActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => { // eslint-disable-line no-unused-vars
  const [activeTab, setActiveTab] = useState('questions');
  const [content, setContent] = useState({
    questions: [{
      question: {
        languageText: '',
        languageAudio: null,
        englishText: '',
        englishAudio: null
      },
      answer: {
        sentences: [{
          languageText: '',
          languageAudio: null,
          englishText: '',
          englishAudio: null
        }],
        image: null
      },
      keywords: [{
        languageText: '',
        languageAudio: null,
        englishText: '',
        englishAudio: null,
        confusingLetters: ''
      }]
    }]
  });
  
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioElements, setAudioElements] = useState({});
  const [translating, setTranslating] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);

  // Initialize content from props
  useEffect(() => {
    if (initialContent && initialContent.trim() !== '') {
      try {
        const parsed = JSON.parse(initialContent);
        
        // Ensure parsed content has the correct structure
        if (parsed && parsed.questions && Array.isArray(parsed.questions) && parsed.questions.length > 0) {
          // Validate that each question has the required nested structure
          const isValid = parsed.questions.every(q => 
            q.question && 
            q.answer && 
            q.answer.sentences && 
            Array.isArray(q.answer.sentences) &&
            q.answer.sentences.length > 0
          );
          
          if (isValid) {
            // Ensure keywords array exists (for backward compatibility)
            setContent({
              ...parsed,
              keywords: parsed.keywords || []
            });
          }
        }
      } catch (error) {
        // Ignore parse errors
      }
    }
  }, [initialContent]);

  // Notify parent of content changes
  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  // Add a keyword to a specific question
  const addKeyword = (questionIndex) => {
    setContent(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        keywords: [
          ...(newQuestions[questionIndex].keywords || []),
          {
            languageText: '',
            languageAudio: null,
            englishText: '',
            englishAudio: null,
            confusingLetters: ''
          }
        ]
      };
      return { ...prev, questions: newQuestions };
    });
  };

  // Remove a keyword from a specific question
  const removeKeyword = (questionIndex, keywordIndex) => {
    setContent(prev => {
      const newQuestions = [...prev.questions];
      const keywords = newQuestions[questionIndex].keywords || [];
      
      if (keywords.length <= 1) return prev; // Keep at least one keyword
      
      const newKeywords = [...keywords];
      newKeywords.splice(keywordIndex, 1);
      
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        keywords: newKeywords
      };
      
      return { ...prev, questions: newQuestions };
    });
  };

  // Update keyword field for a specific question
  const updateKeywordField = (questionIndex, keywordIndex, field, value) => {
    setContent(prev => {
      const newQuestions = [...prev.questions];
      const newKeywords = [...(newQuestions[questionIndex].keywords || [])];
      
      newKeywords[keywordIndex] = {
        ...newKeywords[keywordIndex],
        [field]: value
      };
      
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        keywords: newKeywords
      };
      
      return { ...prev, questions: newQuestions };
    });
  };

  // Handle file upload for keyword fields
  const handleKeywordFileUpload = async (keywordIndex, field, file) => {
    if (!file || !chapterId) return;

    const uploadKey = `k${keywordIndex}-${field}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const language = selectedChapter?.subject?.toLowerCase() || 'language';
      const gradeStr = `grade${grade}`;
      
      const filePath = generateActivityStoragePath({
        language,
        grade: gradeStr,
        chapterId,
        activityName: 'long_question_answers',
        fileName: file.name,
        itemId: `keyword${keywordIndex}`
      });
      
      const fileRef = ref(storage, filePath);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      updateKeywordField(keywordIndex, field, downloadURL);
    } catch (error) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Update an answer field (e.g., image)
  const updateAnswerField = (questionIndex, field, value) => {
    setContent(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        answer: {
          ...newQuestions[questionIndex].answer,
          [field]: value
        }
      };
      return { ...prev, questions: newQuestions };
    });
  };

  // Handle file upload for answer fields (like image)
  const handleAnswerFileUpload = async (questionIndex, field, file) => {
    if (!file || !chapterId) return;

    const uploadKey = `q${questionIndex}-a-${field}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const language = selectedChapter?.subject?.toLowerCase() || 'language';
      const gradeStr = `grade${grade}`;
      
      const filePath = generateActivityStoragePath({
        language,
        grade: gradeStr,
        chapterId,
        activityName: 'long_question_answers',
        fileName: file.name,
        itemId: `question${questionIndex}_answer`
      });
      
      const fileRef = ref(storage, filePath);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      updateAnswerField(questionIndex, field, downloadURL);
    } catch (error) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Add a new question
  const addQuestion = () => {
    setContent(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          question: {
            languageText: '',
            languageAudio: null,
            englishText: '',
            englishAudio: null
          },
          answer: {
            sentences: [{
              languageText: '',
              languageAudio: null,
              englishText: '',
              englishAudio: null
            }],
            image: null
          },
          keywords: [{
            languageText: '',
            languageAudio: null,
            englishText: '',
            englishAudio: null,
            confusingLetters: ''
          }]
        }
      ]
    }));
  };

  // Remove a question
  const removeQuestion = (index) => {
    if (content.questions.length <= 1) return;
    
    const newQuestions = [...content.questions];
    newQuestions.splice(index, 1);
    
    setContent(prev => ({
      ...prev,
      questions: newQuestions
    }));
  };

  // Update question field (for keywords, keyConfusingLetters)
  const updateQuestionMetadata = (questionIndex, field, value) => { // eslint-disable-line no-unused-vars
    setContent(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex] = { ...newQuestions[questionIndex], [field]: value };
      return { ...prev, questions: newQuestions };
    });
  };

  // Update question text/audio fields
  const updateQuestionField = (questionIndex, field, value) => {
    setContent(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        question: {
          ...newQuestions[questionIndex].question,
          [field]: value
        }
      };
      return { ...prev, questions: newQuestions };
    });
  };

  // Update a sentence field
  const updateSentenceField = (questionIndex, sentenceIndex, field, value) => {
    setContent(prev => {
      const newQuestions = [...prev.questions];
      const newSentences = [...newQuestions[questionIndex].answer.sentences];
      
      newSentences[sentenceIndex] = {
        ...newSentences[sentenceIndex],
        [field]: value
      };
      
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        answer: {
          ...newQuestions[questionIndex].answer,
          sentences: newSentences
        }
      };
      
      return { ...prev, questions: newQuestions };
    });
  };

  // Add a sentence to an answer
  const addSentence = (questionIndex) => {
    setContent(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        answer: {
          sentences: [
            ...newQuestions[questionIndex].answer.sentences,
            {
              languageText: '',
              languageAudio: null,
              englishText: '',
              englishAudio: null
            }
          ]
        }
      };
      return { ...prev, questions: newQuestions };
    });
  };

  // Remove a sentence from an answer
  const removeSentence = (questionIndex, sentenceIndex) => {
    setContent(prev => {
      const newQuestions = [...prev.questions];
      const sentences = newQuestions[questionIndex].answer.sentences;
      
      if (sentences.length <= 1) return prev; // Keep at least one sentence
      
      const newSentences = [...sentences];
      newSentences.splice(sentenceIndex, 1);
      
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        answer: {
          sentences: newSentences
        }
      };
      
      return { ...prev, questions: newQuestions };
    });
  };

  // Handle file upload for question fields
  const handleQuestionFileUpload = async (questionIndex, field, file) => {
    if (!file || !chapterId) return;

    const uploadKey = `q${questionIndex}-${field}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const language = selectedChapter?.subject?.toLowerCase() || 'language';
      const gradeStr = `grade${grade}`;
      
      const filePath = generateActivityStoragePath({
        language,
        grade: gradeStr,
        chapterId,
        activityName: 'long_question_answers',
        fileName: file.name,
        itemId: `question${questionIndex}`
      });
      
      const fileRef = ref(storage, filePath);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      updateQuestionField(questionIndex, field, downloadURL);
    } catch (error) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Handle file upload for sentence fields
  const handleSentenceFileUpload = async (questionIndex, sentenceIndex, field, file) => {
    if (!file || !chapterId) return;

    const uploadKey = `q${questionIndex}-s${sentenceIndex}-${field}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const language = selectedChapter?.subject?.toLowerCase() || 'language';
      const gradeStr = `grade${grade}`;
      
      const filePath = generateActivityStoragePath({
        language,
        grade: gradeStr,
        chapterId,
        activityName: 'long_question_answers',
        fileName: file.name,
        itemId: `question${questionIndex}_sentence${sentenceIndex}`
      });
      
      const fileRef = ref(storage, filePath);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      updateSentenceField(questionIndex, sentenceIndex, field, downloadURL);
    } catch (error) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Translate all language text to English
  const translateAllText = async () => {
    setTranslating({ all: true });

    try {
      // Collect all texts to translate
      const textsToTranslate = [];
      const translationMap = [];

      content.questions.forEach((questionItem, qIndex) => {
        // Question text
        if (questionItem.question.languageText && !questionItem.question.englishText) {
          textsToTranslate.push(questionItem.question.languageText);
          translationMap.push({ type: 'question', qIndex });
        }

        // Sentence texts
        questionItem.answer.sentences.forEach((sentence, sIndex) => {
          if (sentence.languageText && !sentence.englishText) {
            textsToTranslate.push(sentence.languageText);
            translationMap.push({ type: 'sentence', qIndex, sIndex });
          }
        });
      });

      // Keyword texts
      content.questions.forEach((questionItem, qIndex) => {
        const keywords = questionItem.keywords || [];
        keywords.forEach((keyword, kIndex) => {
          if (keyword.languageText && !keyword.englishText) {
            textsToTranslate.push(keyword.languageText);
            translationMap.push({ type: 'keyword', qIndex, kIndex });
          }
        });
      });

      if (textsToTranslate.length === 0) {
        alert('All text already has English translations or no language text to translate.');
        setTranslating({});
        return;
      }

      // Translate all texts in batch
      const translatedTexts = await translateBatchToText(
        textsToTranslate,
        db,
        5,
        2000,
        Constants.LANGUAGES.KANNADA,
        Constants.LANGUAGES.ENGLISH
      );

      // Update content with translations
      setContent(prev => {
        const newQuestions = JSON.parse(JSON.stringify(prev.questions)); // Deep clone

        translationMap.forEach((mapping, index) => {
          const translatedText = translatedTexts[index];
          if (translatedText && !translatedText.startsWith('[Translation Error:')) {
            if (mapping.type === 'question') {
              newQuestions[mapping.qIndex].question.englishText = translatedText;
            } else if (mapping.type === 'sentence') {
              newQuestions[mapping.qIndex].answer.sentences[mapping.sIndex].englishText = translatedText;
            } else if (mapping.type === 'keyword') {
              // Keywords are now per question, so update the specific question's keywords
              if (!newQuestions[mapping.qIndex].keywords) {
                newQuestions[mapping.qIndex].keywords = [];
              }
              newQuestions[mapping.qIndex].keywords[mapping.kIndex].englishText = translatedText;
            }
          }
        });

        return { ...prev, questions: newQuestions };
      });

      alert(`Successfully translated ${translatedTexts.filter(t => t && !t.startsWith('[Translation Error:')).length} items!`);
    } catch (error) {
      alert('Failed to translate: ' + error.message);
    } finally {
      setTranslating({});
    }
  };

  // Generate audio for all text
  const generateAllAudio = async () => {
    setGeneratingAudio(true);

    try {
      const storage = getStorage();
      const ttsRequests = [];

      content.questions.forEach((questionItem, qIndex) => {
        // Question language audio
        if (questionItem.question.languageText && questionItem.question.languageText.trim() && !questionItem.question.languageAudio) {
          ttsRequests.push({
            text: questionItem.question.languageText,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            qIndex,
            type: 'questionLanguage'
          });
        }

        // Question English audio
        if (questionItem.question.englishText && questionItem.question.englishText.trim() && !questionItem.question.englishAudio) {
          ttsRequests.push({
            text: questionItem.question.englishText,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            qIndex,
            type: 'questionEnglish'
          });
        }

        // Sentence audios
        questionItem.answer.sentences.forEach((sentence, sIndex) => {
          // Sentence language audio
          if (sentence.languageText && sentence.languageText.trim() && !sentence.languageAudio) {
            ttsRequests.push({
              text: sentence.languageText,
              languageCode: LanguageCodes.KANNADA,
              ssmlGender: VoiceGenders.FEMALE,
              qIndex,
              sIndex,
              type: 'sentenceLanguage'
            });
          }

          // Sentence English audio
          if (sentence.englishText && sentence.englishText.trim() && !sentence.englishAudio) {
            ttsRequests.push({
              text: sentence.englishText,
              languageCode: LanguageCodes.ENGLISH,
              ssmlGender: VoiceGenders.FEMALE,
              qIndex,
              sIndex,
              type: 'sentenceEnglish'
            });
          }
        });
      });

      // Keyword audios - now per question
      content.questions.forEach((questionItem, qIndex) => {
        const keywords = questionItem.keywords || [];
        keywords.forEach((keyword, kIndex) => {
          // Keyword language audio
          if (keyword.languageText && keyword.languageText.trim() && !keyword.languageAudio) {
            ttsRequests.push({
              text: keyword.languageText,
              languageCode: LanguageCodes.KANNADA,
              ssmlGender: VoiceGenders.FEMALE,
              qIndex,
              kIndex,
              type: 'keywordLanguage'
            });
          }

          // Keyword English audio
          if (keyword.englishText && keyword.englishText.trim() && !keyword.englishAudio) {
            ttsRequests.push({
              text: keyword.englishText,
              languageCode: LanguageCodes.ENGLISH,
              ssmlGender: VoiceGenders.FEMALE,
              qIndex,
              kIndex,
              type: 'keywordEnglish'
            });
          }
        });
      });

      if (ttsRequests.length === 0) {
        alert('All text already has audio or no text to generate audio for.');
        setGeneratingAudio(false);
        return;
      }

      // Generate audio for all requests
      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map(req => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );

      // Update content with audio URLs
      setContent(prev => {
        const newQuestions = JSON.parse(JSON.stringify(prev.questions)); // Deep clone

        ttsRequests.forEach((request, index) => {
          const audioUrl = audioUrls[index];
          if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
            const { qIndex, sIndex, kIndex, type } = request;
            
            if (type === 'questionLanguage') {
              newQuestions[qIndex].question.languageAudio = audioUrl;
            } else if (type === 'questionEnglish') {
              newQuestions[qIndex].question.englishAudio = audioUrl;
            } else if (type === 'sentenceLanguage') {
              newQuestions[qIndex].answer.sentences[sIndex].languageAudio = audioUrl;
            } else if (type === 'sentenceEnglish') {
              newQuestions[qIndex].answer.sentences[sIndex].englishAudio = audioUrl;
            } else if (type === 'keywordLanguage') {
              // Keywords are now per question
              if (!newQuestions[qIndex].keywords) {
                newQuestions[qIndex].keywords = [];
              }
              newQuestions[qIndex].keywords[kIndex].languageAudio = audioUrl;
            } else if (type === 'keywordEnglish') {
              // Keywords are now per question
              if (!newQuestions[qIndex].keywords) {
                newQuestions[qIndex].keywords = [];
              }
              newQuestions[qIndex].keywords[kIndex].englishAudio = audioUrl;
            }
          }
        });

        return { ...prev, questions: newQuestions };
      });

      alert(`Successfully generated ${audioUrls.filter(url => url && !url.startsWith('[TTS Error:')).length} audio files!`);
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Play audio
  const playAudio = async (audioUrl) => {
    if (!audioUrl) return;
    
    try {
      // Stop any currently playing audio
      if (playingAudio) {
        audioElements[playingAudio]?.pause();
        if (playingAudio === audioUrl) {
          setPlayingAudio(null);
          return;
        }
      }

      // For Firebase Storage URLs, try to get a fresh download URL
      if (audioUrl.includes('firebasestorage.googleapis.com')) {
        const urlParts = audioUrl.split('/o/')[1];
        if (urlParts) {
          const storagePath = decodeURIComponent(urlParts.split('?')[0]);
          const storage = getStorage();
          const audioRef = ref(storage, storagePath);
          const freshDownloadURL = await getDownloadURL(audioRef);
          
          const audio = new Audio(freshDownloadURL);
          audioElements[audioUrl] = audio;
          setAudioElements({...audioElements});
          
          audio.onended = () => setPlayingAudio(null);
          await audio.play();
          setPlayingAudio(audioUrl);
          return;
        }
      }
      
      // Direct URL fallback
      const audio = new Audio(audioUrl);
      audioElements[audioUrl] = audio;
      setAudioElements({...audioElements});
      
      audio.onended = () => setPlayingAudio(null);
      await audio.play();
      setPlayingAudio(audioUrl);
    } catch (error) {
      // Ignore audio play errors
    }
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('questions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'questions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📝 Questions & Answers
          </button>
        </nav>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={translateAllText}
          disabled={translating.all || generatingAudio}
          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:bg-purple-400 disabled:cursor-not-allowed"
        >
          {translating.all ? (
            <>
              <FaSpinner className="mr-2 h-4 w-4 animate-spin" />
              Translating...
            </>
          ) : (
            <>
              <FaLanguage className="mr-2 h-4 w-4" />
              Translate All to English
            </>
          )}
        </button>
        
        <button
          type="button"
          onClick={generateAllAudio}
          disabled={generatingAudio || translating.all}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors disabled:bg-orange-400 disabled:cursor-not-allowed"
        >
          {generatingAudio ? (
            <>
              <FaSpinner className="mr-2 h-4 w-4 animate-spin" />
              Generating Audio...
            </>
          ) : (
            <>
              <FaVolumeUp className="mr-2 h-4 w-4" />
              Generate All Audio
            </>
          )}
        </button>
      </div>

      {/* Questions Tab Content */}
      {activeTab === 'questions' && (
        <>
          {content.questions && content.questions.map((questionItem, qIndex) => (
        <div key={qIndex} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          {/* Question Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Question {qIndex + 1}</h3>
            {content.questions.length > 1 && (
              <button
                type="button"
                onClick={() => removeQuestion(qIndex)}
                className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                title="Remove Question"
              >
                <FaTrash className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Question Row - Compact Single Line */}
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0">
                Q
              </div>
              
              {/* Question Text Fields */}
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                  placeholder="Language question text *"
                  value={questionItem.question.languageText}
                  onChange={(e) => updateQuestionField(qIndex, 'languageText', e.target.value)}
                />
                <input
                  type="text"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                  placeholder="English question text"
                  value={questionItem.question.englishText}
                  onChange={(e) => updateQuestionField(qIndex, 'englishText', e.target.value)}
                />
              </div>
              
              {/* Question Audio Controls */}
              <div className="flex items-center gap-2">
                {/* Language Audio */}
                <div className="flex items-center gap-1">
                  <input
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    id={`q${qIndex}-lang-audio`}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleQuestionFileUpload(qIndex, 'languageAudio', file);
                    }}
                    disabled={uploadingFiles[`q${qIndex}-languageAudio`]}
                  />
                  <label
                    htmlFor={`q${qIndex}-lang-audio`}
                    className="cursor-pointer px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                    title="Language Audio"
                  >
                    🎵 LA
                  </label>
                  {uploadingFiles[`q${qIndex}-languageAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                  {questionItem.question.languageAudio && !uploadingFiles[`q${qIndex}-languageAudio`] && (
                    <>
                      <button
                        type="button"
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                        onClick={() => playAudio(questionItem.question.languageAudio)}
                        title="Play Language Audio"
                      >
                        <FaVolumeUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        onClick={() => updateQuestionField(qIndex, 'languageAudio', null)}
                        title="Remove Language Audio"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
                
                {/* English Audio */}
                <div className="flex items-center gap-1">
                  <input
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    id={`q${qIndex}-eng-audio`}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleQuestionFileUpload(qIndex, 'englishAudio', file);
                    }}
                    disabled={uploadingFiles[`q${qIndex}-englishAudio`]}
                  />
                  <label
                    htmlFor={`q${qIndex}-eng-audio`}
                    className="cursor-pointer px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                    title="English Audio"
                  >
                    🎵 EA
                  </label>
                  {uploadingFiles[`q${qIndex}-englishAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                  {questionItem.question.englishAudio && !uploadingFiles[`q${qIndex}-englishAudio`] && (
                    <>
                      <button
                        type="button"
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                        onClick={() => playAudio(questionItem.question.englishAudio)}
                        title="Play English Audio"
                      >
                        <FaVolumeUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        onClick={() => updateQuestionField(qIndex, 'englishAudio', null)}
                        title="Remove English Audio"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Answer Sentences Section */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-4">
                <h4 className="text-sm font-semibold text-gray-700">Answer Sentences</h4>
                
                {/* Answer Level Image */}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id={`q${qIndex}-answer-image`}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleAnswerFileUpload(qIndex, 'image', file);
                    }}
                    disabled={uploadingFiles[`q${qIndex}-a-image`]}
                  />
                  <label
                    htmlFor={`q${qIndex}-answer-image`}
                    className="cursor-pointer px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-200 transition-colors flex items-center gap-1.5"
                    title="Upload Answer Image"
                  >
                    🖼️ Answer Image
                  </label>
                  {uploadingFiles[`q${qIndex}-a-image`] && <FaSpinner className="w-3 h-3 animate-spin text-purple-600" />}
                  {questionItem.answer.image && !uploadingFiles[`q${qIndex}-a-image`] && (
                    <>
                      <button
                        type="button"
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        onClick={async () => {
                          try {
                            const storage = getStorage();
                            const imageRef = ref(storage, questionItem.answer.image);
                            const downloadURL = await getDownloadURL(imageRef);
                            window.open(downloadURL, '_blank');
                          } catch (error) {
                            alert('Failed to preview image');
                          }
                        }}
                        title="Preview Image"
                      >
                        <FaEye className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        onClick={() => updateAnswerField(qIndex, 'image', null)}
                        title="Remove Image"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => addSentence(qIndex)}
                className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                <FaPlus className="mr-1 h-3 w-3" />
                Add Sentence
              </button>
            </div>
            
            <div className="space-y-2">
              {questionItem.answer.sentences.map((sentence, sIndex) => (
                <div key={sIndex} className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0">
                      {sIndex + 1}
                    </div>
                    
                    {/* Sentence Text Fields */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm"
                        placeholder="Language sentence text *"
                        value={sentence.languageText}
                        onChange={(e) => updateSentenceField(qIndex, sIndex, 'languageText', e.target.value)}
                      />
                      <input
                        type="text"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm"
                        placeholder="English sentence text"
                        value={sentence.englishText}
                        onChange={(e) => updateSentenceField(qIndex, sIndex, 'englishText', e.target.value)}
                      />
                    </div>
                    
                    {/* Sentence Audio Controls */}
                    <div className="flex items-center gap-2">
                      {/* Language Audio */}
                      <div className="flex items-center gap-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          id={`q${qIndex}-s${sIndex}-lang-audio`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleSentenceFileUpload(qIndex, sIndex, 'languageAudio', file);
                          }}
                          disabled={uploadingFiles[`q${qIndex}-s${sIndex}-languageAudio`]}
                        />
                        <label
                          htmlFor={`q${qIndex}-s${sIndex}-lang-audio`}
                          className="cursor-pointer px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 transition-colors"
                          title="Language Audio"
                        >
                          🎵 LA
                        </label>
                        {uploadingFiles[`q${qIndex}-s${sIndex}-languageAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-green-600" />}
                        {sentence.languageAudio && !uploadingFiles[`q${qIndex}-s${sIndex}-languageAudio`] && (
                          <>
                            <button
                              type="button"
                              className="p-1 text-green-600 hover:bg-green-100 rounded"
                              onClick={() => playAudio(sentence.languageAudio)}
                              title="Play Language Audio"
                            >
                              <FaVolumeUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              onClick={() => updateSentenceField(qIndex, sIndex, 'languageAudio', null)}
                              title="Remove Language Audio"
                            >
                              <FaTrash className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                      
                      {/* English Audio */}
                      <div className="flex items-center gap-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          id={`q${qIndex}-s${sIndex}-eng-audio`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleSentenceFileUpload(qIndex, sIndex, 'englishAudio', file);
                          }}
                          disabled={uploadingFiles[`q${qIndex}-s${sIndex}-englishAudio`]}
                        />
                        <label
                          htmlFor={`q${qIndex}-s${sIndex}-eng-audio`}
                          className="cursor-pointer px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 transition-colors"
                          title="English Audio"
                        >
                          🎵 EA
                        </label>
                        {uploadingFiles[`q${qIndex}-s${sIndex}-englishAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-green-600" />}
                        {sentence.englishAudio && !uploadingFiles[`q${qIndex}-s${sIndex}-englishAudio`] && (
                          <>
                            <button
                              type="button"
                              className="p-1 text-green-600 hover:bg-green-100 rounded"
                              onClick={() => playAudio(sentence.englishAudio)}
                              title="Play English Audio"
                            >
                              <FaVolumeUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              onClick={() => updateSentenceField(qIndex, sIndex, 'englishAudio', null)}
                              title="Remove English Audio"
                            >
                              <FaTrash className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                      
                      {/* Remove Sentence Button */}
                      {questionItem.answer.sentences.length > 1 && (
                        <button
                          type="button"
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                          onClick={() => removeSentence(qIndex, sIndex)}
                          title="Remove Sentence"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Keywords Section */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-semibold text-gray-700">Keywords</h4>
              <button
                type="button"
                onClick={() => addKeyword(qIndex)}
                className="inline-flex items-center px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <FaPlus className="mr-1 h-3 w-3" />
                Add Keyword
              </button>
            </div>
            
            <div className="space-y-2">
              {(questionItem.keywords || []).map((keyword, kIndex) => (
                <div key={kIndex} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0">
                      {kIndex + 1}
                    </div>
                    
                    {/* Keyword Text Fields */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input
                        type="text"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm"
                        placeholder="Language keyword text *"
                        value={keyword.languageText}
                        onChange={(e) => updateKeywordField(qIndex, kIndex, 'languageText', e.target.value)}
                      />
                      <input
                        type="text"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm"
                        placeholder="English keyword text"
                        value={keyword.englishText}
                        onChange={(e) => updateKeywordField(qIndex, kIndex, 'englishText', e.target.value)}
                      />
                      <input
                        type="text"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm"
                        placeholder="Confusing letters (e.g., ಕ,ಖ,ಗ)"
                        value={keyword.confusingLetters || ''}
                        onChange={(e) => updateKeywordField(qIndex, kIndex, 'confusingLetters', e.target.value)}
                      />
                    </div>
                    
                    {/* Keyword Audio Controls */}
                    <div className="flex items-center gap-2">
                      {/* Language Audio */}
                      <div className="flex items-center gap-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          id={`q${qIndex}-k${kIndex}-lang-audio`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleKeywordFileUpload(qIndex, kIndex, 'languageAudio', file);
                          }}
                          disabled={uploadingFiles[`q${qIndex}-k${kIndex}-languageAudio`]}
                        />
                        <label
                          htmlFor={`q${qIndex}-k${kIndex}-lang-audio`}
                          className="cursor-pointer px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 transition-colors"
                          title="Language Audio"
                        >
                          🎵 LA
                        </label>
                        {uploadingFiles[`q${qIndex}-k${kIndex}-languageAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-purple-600" />}
                        {keyword.languageAudio && !uploadingFiles[`q${qIndex}-k${kIndex}-languageAudio`] && (
                          <>
                            <button
                              type="button"
                              className="p-1 text-green-600 hover:bg-green-100 rounded"
                              onClick={() => playAudio(keyword.languageAudio)}
                              title="Play Language Audio"
                            >
                              <FaVolumeUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              onClick={() => updateKeywordField(qIndex, kIndex, 'languageAudio', null)}
                              title="Remove Language Audio"
                            >
                              <FaTrash className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                      
                      {/* English Audio */}
                      <div className="flex items-center gap-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          id={`q${qIndex}-k${kIndex}-eng-audio`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleKeywordFileUpload(qIndex, kIndex, 'englishAudio', file);
                          }}
                          disabled={uploadingFiles[`q${qIndex}-k${kIndex}-englishAudio`]}
                        />
                        <label
                          htmlFor={`q${qIndex}-k${kIndex}-eng-audio`}
                          className="cursor-pointer px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 transition-colors"
                          title="English Audio"
                        >
                          🎵 EA
                        </label>
                        {uploadingFiles[`q${qIndex}-k${kIndex}-eng-audio`] && <FaSpinner className="w-3 h-3 animate-spin text-purple-600" />}
                        {keyword.englishAudio && !uploadingFiles[`q${qIndex}-k${kIndex}-englishAudio`] && (
                          <>
                            <button
                              type="button"
                              className="p-1 text-green-600 hover:bg-green-100 rounded"
                              onClick={() => playAudio(keyword.englishAudio)}
                              title="Play English Audio"
                            >
                              <FaVolumeUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              onClick={() => updateKeywordField(qIndex, kIndex, 'englishAudio', null)}
                              title="Remove English Audio"
                            >
                              <FaTrash className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>

                      {/* Remove Keyword Button */}
                      {(questionItem.keywords || []).length > 1 && (
                        <button
                          type="button"
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                          onClick={() => removeKeyword(qIndex, kIndex)}
                          title="Remove Keyword"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      
          <div className="flex justify-center">
            <button
              type="button"
              onClick={addQuestion}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FaPlus className="-ml-1 mr-2 h-4 w-4" />
              Add Question
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Validation function
export const validateLongQuestionAnswersContent = (content) => {
  const errors = [];
  
  // Parse if content is a string
  let parsedContent = content;
  if (typeof content === 'string') {
    try {
      parsedContent = JSON.parse(content);
    } catch (error) {
      errors.push('Invalid JSON format');
      return errors;
    }
  }
  
  if (!parsedContent || typeof parsedContent !== 'object') {
    errors.push('Content must be an object');
    return errors;
  }
  
  // Use parsedContent for the rest of validation
  content = parsedContent;
  
  if (!content.questions || !Array.isArray(content.questions)) {
    errors.push('Questions must be an array');
    return errors;
  }
  
  if (content.questions.length === 0) {
    errors.push('At least one question is required');
    return errors;
  }
  
  content.questions.forEach((questionItem, index) => {
    const questionNum = index + 1;
    
    // Validate question structure
    if (!questionItem.question) {
      errors.push(`Question ${questionNum}: Question object is missing`);
      return;
    }
    
    if (!questionItem.answer || !questionItem.answer.sentences) {
      errors.push(`Question ${questionNum}: Answer sentences are missing`);
      return;
    }
    
    // Validate question text
    if (!questionItem.question.languageText && !questionItem.question.englishText) {
      errors.push(`Question ${questionNum}: At least one question text (Language or English) is required`);
    }
    
    // Validate answer sentences
    if (questionItem.answer.sentences.length === 0) {
      errors.push(`Question ${questionNum}: At least one answer sentence is required`);
    }
    
    questionItem.answer.sentences.forEach((sentence, sIndex) => {
      const sentenceNum = sIndex + 1;
      if (!sentence.languageText && !sentence.englishText) {
        errors.push(`Question ${questionNum}, Sentence ${sentenceNum}: At least one text (Language or English) is required`);
      }
      
      // Validate English text: must start with capital letter
      if (sentence.englishText?.trim()) {
        const trimmed = sentence.englishText.trim();
        if (!/^[A-Z]/.test(trimmed)) {
          errors.push(`Question ${questionNum}, Sentence ${sentenceNum}: English text must start with a capital letter`);
        }
      }
    });
    
    // Validate question English text
    if (questionItem.question.englishText?.trim()) {
      const trimmed = questionItem.question.englishText.trim();
      if (!/^[A-Z]/.test(trimmed)) {
        errors.push(`Question ${questionNum}: English question must start with a capital letter`);
      }
    }
  });
  
  // Validate keywords (optional but if present, must have text)
  if (content.keywords && Array.isArray(content.keywords)) {
    content.keywords.forEach((keyword, index) => {
      const keywordNum = index + 1;
      if (!keyword.languageText && !keyword.englishText) {
        errors.push(`Keyword ${keywordNum}: At least one text (Language or English) is required`);
      }
    });
  }
  
  return errors;
};

export default LongQuestionAnswersActivity;
